import { Code, Stack, Tabs, Text, Title } from '@mantine/core';
import { MetadataRow } from './MetadataRow';
import { CODE_BLOCK_STYLE } from './styles';
import { DetectedEntity } from './types';

function ClassPanel({ entityType, entities }: { entityType: string; entities: DetectedEntity[] }) {
  const firstEntity = entities[0];
  const label = firstEntity.entityLabel || entityType || 'Unknown';
  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={3}>{label}</Title>
        <Text size="sm" c="dimmed">
          {entities.length} {entities.length === 1 ? 'entity' : 'entities'} extracted
        </Text>
      </Stack>

      <Stack gap="xs">
        <MetadataRow label="Entity type" value={entityType} />
        <MetadataRow label="Model" value={firstEntity.llmModelName} />
        <MetadataRow label="Finish reason" value={firstEntity.llmFinishReason} />
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Detected {label.toLowerCase()} entities</Title>
        {entities.map((detected, index) => (
          <Stack key={detected.id} gap={4}>
            <Text size="xs" c="dimmed" fw={600}>
              {label} {index + 1} of {entities.length}
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {JSON.stringify(detected.entity.fields, null, 2)}
            </Code>
          </Stack>
        ))}
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Reasoning for {label.toLowerCase()}</Title>
        {firstEntity.llmReasoningContent ? (
          <Code block style={CODE_BLOCK_STYLE}>
            {firstEntity.llmReasoningContent}
          </Code>
        ) : (
          <Text c="dimmed" size="sm">
            No reasoning captured (non-thinking model, or detection ran before reasoning capture was
            enabled)
          </Text>
        )}
      </Stack>
    </Stack>
  );
}

function groupByEntityType(entities: DetectedEntity[]): Map<string, DetectedEntity[]> {
  const groups = new Map<string, DetectedEntity[]>();
  for (const detected of entities) {
    if (!detected) continue;
    const key = detected.entityType ?? 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(detected);
    } else {
      groups.set(key, [detected]);
    }
  }
  return groups;
}

export function EntityDetectionPanel({ entities }: { entities: DetectedEntity[] }) {
  const safeEntities = entities.filter(Boolean);
  if (safeEntities.length === 0) {
    return <Text c="dimmed">No entities detected.</Text>;
  }

  const groups = Array.from(groupByEntityType(safeEntities).entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const [defaultTabValue] = groups[0];

  return (
    <Tabs defaultValue={defaultTabValue} orientation="vertical">
      <Tabs.List style={{ minWidth: 220, maxWidth: 260 }}>
        {groups.map(([entityType, group]) => {
          const label = group[0].entityLabel || entityType;
          return (
            <Tabs.Tab key={entityType} value={entityType}>
              {label} ({group.length})
            </Tabs.Tab>
          );
        })}
      </Tabs.List>
      {groups.map(([entityType, group]) => (
        <Tabs.Panel key={entityType} value={entityType} pl="md">
          <ClassPanel entityType={entityType} entities={group} />
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
